import { css } from '@emotion/css'
import { spacing, breakpoint, fontSize, borderRadius, fontWeight, mediaQueryHelper, shorthandHelper, colors } from '@offerzen/pinata'
import blueArrow from '../../../assets/blue-arrow-round.svg'
import legacyColors from '../../lib/themes/colors'
import { headerHeight } from '../../components/Header/styles'

const leftColumnOffsetBase = 120
const leftColumnOffsetShortScreen = 90
const leftColumnOffsetShortestScreen = 30
const leftColumnWidth = 580

const styles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
    backgroundColor: legacyColors.darkEclipse,
    minHeight: `calc(100vh - ${headerHeight}px)`,
    [mediaQueryHelper(breakpoint.tablet)]: {
      marginTop: headerHeight,
    },
    [mediaQueryHelper(500, 'max-height')]: {
      marginTop: 0,
      minHeight: '100vh',
    },
  }),

  recaptchaNotice: css({
    width: '100%',
    marginTop: spacing.extraLarge,
    color: colors.geyser,
    textAlign: 'center',

    a: {
      color: colors.geyser,
      textDecoration: 'underline',
    },

    [mediaQueryHelper(breakpoint.tablet)]: {
      padding: shorthandHelper([0, spacing.large]),
      paddingBottom: spacing.extraLarge,
    },
  }),

  recaptchaNoticeContainer: css({
    borderTop: '1px solid rgba(41, 139, 242, 0.2)',
    padding: shorthandHelper([spacing.extraLarge, spacing.large]),
    paddingBottom: 120,
    textAlign: 'left',
  }),

  card: css({
    background: 'white',
    width: '100%',
    flex: '1 0 auto',
    borderRadius: 0,
    padding: shorthandHelper([spacing.extraLarge, spacing.large]),
    marginBottom: 0,
    [mediaQueryHelper(breakpoint.tablet)]: {
      padding: spacing.extraLarge,
      flex: '0 0 auto',
      boxShadow: `0 1px 5px ${colors.boxShadow}`,
      marginTop: 0,
      minHeight: 'auto',
      borderRadius: borderRadius.medium,
    },
    [mediaQueryHelper(1240)]: {
      width: leftColumnWidth,
    },
  }),

  headerSection: css({
    h3: {
      fontSize: fontSize.extraLarge,
      fontWeight: fontWeight.medium,
      marginBottom: 0,
      color: colors.blackPearl,
    },
    a: {
      color: colors.cornflowerBlue,
    },
  }),

  login: css({
    marginTop: spacing.small,
    marginBottom: 32, // Pinata has no corresponding value
  }),

  disclaimer: css({
    marginTop: spacing.medium,
    fontSize: fontSize.default,
    marginBottom: 0,
    a: {
      color: colors.cornflowerBlue,
    },
  }),

  listLabel: css({
    color: 'white',
    width: '100%',
    textAlign: 'left',
    fontSize: fontSize.large,
    fontWeight: fontWeight.bold,
    marginTop: spacing.large,
    [mediaQueryHelper(breakpoint.phone)]: {
      fontWeight: fontWeight.medium,
    },
  }),

  infoList: css({
    listStyle: 'none',
    padding: 0,
    width: '100%',

    li: {
      color: 'white',
      fontWeight: fontWeight.regular,
      fontSize: fontSize.default,
      paddingBottom: spacing.medium,
      position: 'relative',
      paddingLeft: 34,
      lineHeight: '150%',

      '::before': {
        content: '""',
        width: 20,
        height: 20,
        background: `url(${blueArrow})`,
        backgroundRepeat: 'no-repeat',
        left: 0,
        position: 'absolute',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        top: 1, // visual alignment with letters
      },
    },
    [mediaQueryHelper(breakpoint.desktop)]: {
      marginTop: spacing.large,
      padding: 0,
    },
  }),

  columnContainer: css({
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    width: '100%',
    alignItems: 'stretch',

    [mediaQueryHelper(1240)]: {
      width: '1200px',
    },

    '> div:first-child': {
      minHeight: '960px',
      width: '40%',
      maxWidth: '40%',

      h1: {
        color: 'white',
        marginTop: 60,
        fontWeight: fontWeight.semiBold,
        fontSize: 32,
        lineHeight: '130%',
        [mediaQueryHelper(720, 'max-height')]: {
          marginTop: 30,
        },
      },

      [mediaQueryHelper(breakpoint.tablet)]: {
        width: '40%',
        maxWidth: '40%',
      },
      [mediaQueryHelper(breakpoint.desktop)]: {
        width: '50%',
        maxWidth: '50%',
      },
      [mediaQueryHelper(1240)]: {
        width: leftColumnWidth,
        maxWidth: leftColumnWidth,
      },

      [mediaQueryHelper(720, 'max-height')]: {
        paddingTop: 60,
      },
    },
    '> div:last-child': {
      width: '60%',
      top: 0,
      bottom: 0,
      left: '40%',
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      paddingRight: 60,
      paddingTop: 120,
      [mediaQueryHelper(breakpoint.tablet)]: {
        left: '40%',
        width: '60%',
        paddingRight: spacing.large,
      },
      [mediaQueryHelper(breakpoint.desktop)]: {
        left: '50%',
        width: '50%',
      },
      [mediaQueryHelper(1240)]: {
        left: leftColumnWidth,
        width: leftColumnWidth,
        maxWidth: leftColumnWidth,
      },
      [mediaQueryHelper(720, 'max-height')]: {
        paddingTop: 60,
      },
    },
  }),

  leftColumn: css({
    position: 'sticky',
    top: leftColumnOffsetBase,
    [mediaQueryHelper(720, 'max-height')]: {
      top: leftColumnOffsetShortScreen,
    },
    [mediaQueryHelper(500, 'max-height')]: {
      position: 'relative',
      top: leftColumnOffsetShortestScreen,
    },
  }),

  leftColumnContent: css({
    overflow: 'hidden',
    maxHeight: `calc(100vh - ${leftColumnOffsetBase}px)`,
    [mediaQueryHelper(720, 'max-height')]: {
      maxHeight: `calc(100vh - ${leftColumnOffsetShortScreen}px)`,
    },
    [mediaQueryHelper(500, 'max-height')]: {
      maxHeight: 'unset',
    },
    [mediaQueryHelper(breakpoint.tablet)]: {
      paddingLeft: spacing.large,
      paddingRight: spacing.extraLarge,
    },
    [mediaQueryHelper(breakpoint.desktop)]: {
      paddingLeft: spacing.large,
      paddingRight: spacing.extraLarge,
    },
    [mediaQueryHelper(1240)]: {
      paddingLeft: 60,
      marginRight: 80,
    },
  }),

  featuredImage: css({
    backgroundRepeat: 'no-repeat',
    width: 300,
    position: 'absolute',
    bottom: 0,
    right: '-20%',

    // Added these harcoded breakpoint to fix overlapping on tiny devices
    [mediaQueryHelper(320)]: {
      right: '-50%',
    },
    [mediaQueryHelper(375)]: {
      right: '-15%',
    },
    [mediaQueryHelper(414)]: {
      right: '-10%',
    },
    [mediaQueryHelper(breakpoint.phone)]: {
      width: 350,
      right: 0,
    },
    [mediaQueryHelper(breakpoint.tablet)]: {
      position: 'relative',
      bottom: 'unset',
      right: 'unset',
      width: 500,
      maxWidth: '120%',
      transform: 'translateX(-55px)',
    },
    [mediaQueryHelper(breakpoint.desktop)]: {
      transform: 'translateX(-40px)',
    },
    [mediaQueryHelper(1240)]: {
      transform: 'translateX(-65px)',
    },
  }),

  mobileContainer: css({
    paddingTop: 0,
  }),

  mobileHeader: css({
    position: 'relative',
    backgroundColor: 'white',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%',
    minHeight: 'auto',
    borderBottom: `1px solid ${colors.geyser}`,

    h1: {
      fontWeight: fontWeight.bold,
      fontSize: fontSize.large,
      width: '50%',
      padding: shorthandHelper([spacing.extraLarge, spacing.large]),
      color: colors.blackPearl,
      // Added these harcoded breakpoint to fix overlapping on tiny devices
      [mediaQueryHelper(320)]: {
        width: '70%',
      },
      [mediaQueryHelper(375)]: {
        width: '55%',
      },
      [mediaQueryHelper(414)]: {
        width: '50%',
      },
      [mediaQueryHelper(breakpoint.phone)]: {
        width: '50%',
        fontSize: 32,
        margin: 0,
        fontWeight: fontWeight.semiBold,
      },
    },

    [mediaQueryHelper(breakpoint.phone)]: {
      minHeight: 190,
    },
  }),

  mobileFooter: css({
    padding: spacing.large,
    paddingBottom: spacing.small,
  }),
}

export default styles
